import React, { useCallback, useMemo, useState, useEffect, useContext } from 'react'
import { FormattedMessage } from 'react-intl'

import Container from 'components/Container'
import Row from 'components/Row'
import Col from 'components/Col'
import Button from 'components/Button'
import Checkbox from 'components/Checkbox'
import CheckboxArrowIcon from 'assets/icons/checkbox-arrow-icon'
import ItemsPerPageDropdown from 'components/ItemsPerPageDropdown'
import Pagination from 'features/Pagination'
import Tooltip from 'components/Tooltip'
import InfoIcon from 'assets/icons/info-icon'
import { requestGetSitePressureEcoSys, requestAllThreatTriggers } from 'api/monitoring'
import postRequest from 'utils/postRequest'
import { useNavigate, useParams, useLocation } from 'react-router-dom'
import Loader from 'components/Loader'
import { showErrorToast } from 'utils/toast'

import { BiodiversitySelectorWrapper, PaginationWrapper } from 'components/BiodiversitySelector/style'

import { SpeciesList, ActionsRow, SpeciesRow, ViewButton, ButtonContent, IconSlot, TextSlot } from './style'
import { PageSubtitle, TooltipHintWrapper, PageSubtitleWrapper } from 'flow/siteProposal/pages/about/style'
import { SiteContext } from 'providers/site'
import AddNew from 'assets/icons/add-new'
import ViewIcon from 'assets/icons/view-site-icon'
import CircleIcon from 'assets/icons/circle-icon'

const displayOptions = [
  { label: 'Trigger Ecosystem & Type', value: 'all' },
  { label: 'Ecosystem', value: 'ecosystem' },
  { label: 'Trigger type', value: 'type' }
]
/* Keep your original filter that preserves hierarchy */
const flattenTree = (nodes, filter, depth = 0) => {
  return nodes.flatMap((node) => {
    const matchesFilter = filter === 'all' || node.kind === filter
    const children = node.children ? flattenTree(node.children, filter, depth + 1) : []
    const includeNode = matchesFilter || children.length

    if (!includeNode) {
      return []
    }

    return [
      {
        ...node,
        children,
        depth
      }
    ]
  })
}

/* helpers for building lookup maps and collecting ids */
const buildMaps = (nodes) => {
  const idToNode = new Map()
  const idToParent = new Map()

  const walk = (list, parent = null) => {
    list.forEach((node) => {
      idToNode.set(node.id, node)
      if (parent) idToParent.set(node.id, parent.id)
      if (node.children && node.children.length) walk(node.children, node)
    })
  }

  walk(nodes, null)
  return { idToNode, idToParent }
}

const collectDescendantIds = (node) => {
  const ids = []
  const walk = (n) => {
    ids.push(n.id)
    if (n.children && n.children.length) {
      n.children.forEach((c) => walk(c))
    }
  }
  walk(node)
  return ids
}

const collectAncestorIds = (id, idToParent) => {
  const ancestors = []
  let current = idToParent.get(id)
  while (current) {
    ancestors.push(current)
    current = idToParent.get(current)
  }
  return ancestors
}

const TriggerEcosystemsTab = ({
  onAddToEffect,
  onSelectionChange,
  threatItemId,
  titleValue,
  modalTitleId,
  totalTriggers,
  onCancel
}) => {
  const { site } = useContext(SiteContext)
  // This tab should display only ecosystem triggers by default
  const [typeFilter, setTypeFilter] = useState(displayOptions[0])
  const [pagination, setPagination] = useState({ currentPage: 1, resultsPerPage: 12 })
  const [taxonomicEcoSystem, setTaxonomicEcoSystem] = useState([])
  const [loading, setLoading] = useState(false)
  const [expandedIds, setExpandedIds] = useState(new Set())
  const [selectedIds, setSelectedIds] = useState(new Set())

  const navigate = useNavigate()
  const params = useParams()
  const location = useLocation()

  const getEcosytemData = (page = pagination.currentPage, limit = pagination.resultsPerPage) => {
    setLoading(true)
    requestGetSitePressureEcoSys(params.id, params.monitoringId, threatItemId, page, limit)
      .then((data) => {
        const groups = Array.isArray(data?.data) ? data.data : []

        // store returned nodes for rendering
        setTaxonomicEcoSystem(groups)

        // if API provided pagination, map resultsPerPage/currentPage into local pagination state
        const pageInfo = data?.pagination
        if (pageInfo) {
          setPagination((p) => ({
            ...p,
            resultsPerPage: pageInfo.resultsPerPage ?? p.resultsPerPage,
            currentPage: pageInfo.currentPage ?? p.currentPage
          }))
        }
      })
      .catch((err) => {
        console.error('[TriggerSpeciesTab] requestGetSitePressureSpecies failed', err)
        setTaxonomicEcoSystem([])
      })
      .finally(() => {
        setLoading(false)
      })
  }

  useEffect(() => {
    getEcosytemData()
  }, [])

  // persist selected items across pages if necessary
  const selectedItemsRef = React.useRef(new Map())

  // filtered/flattened tree according to the type filter
  const filteredTree = useMemo(() => flattenTree(taxonomicEcoSystem, typeFilter.value), [taxonomicEcoSystem, typeFilter])

  const { idToNode, idToParent } = useMemo(() => buildMaps(filteredTree), [filteredTree])

  // Fetch existing triggers (filter=2) for this monitor and preselect matching ecosystems
  useEffect(() => {
    const monitorId = Number((threatItemId && (threatItemId.threatItemId ?? threatItemId)) || threatItemId || null)
    if (!monitorId) return
    let mounted = true
    requestAllThreatTriggers(monitorId, 2)
      .then((resp) => {
        if (!mounted) return
        const extractArrayFromResp = (r) => {
          if (!r) return []
          if (Array.isArray(r)) return r
          if (r.data && Array.isArray(r.data)) return r.data
          const seen = new Set()
          const findArray = (obj) => {
            if (!obj || typeof obj !== 'object') return null
            if (seen.has(obj)) return null
            seen.add(obj)
            for (const k of Object.keys(obj)) {
              const v = obj[k]
              if (Array.isArray(v)) return v
              if (v && typeof v === 'object') {
                const nested = findArray(v)
                if (nested) return nested
              }
            }
            return null
          }
          const found = findArray(r)
          return Array.isArray(found) ? found : []
        }
        try {
          const arr = extractArrayFromResp(resp)
          if (arr && arr.length > 0) {
            const ids = new Set()
            arr.forEach((it) => {
              const id = it && (it.siteEcosystemId || it.site_ecosystem_id || it.id || it.historyId)
              if (id != null) ids.add(String(id))
            })
            // map to node ids present in current tree
            const matched = new Set()
            ids.forEach((sid) => {
              // try direct match in idToNode
              if (idToNode && idToNode.has(sid)) matched.add(sid)
              else {
                // try to find a node whose raw fields match
                if (idToNode && idToNode.size) {
                  for (const [nid, node] of idToNode.entries()) {
                    if (!node) continue
                    const raw = node.raw || {}
                    if (String(raw.historyId || raw.siteEcosystemId || raw.site_ecosystem_id || raw.id) === sid) {
                      matched.add(nid)
                      break
                    }
                  }
                }
              }
            })
            if (matched.size > 0) {
              setSelectedIds(new Set(matched))
              try {
                matched.forEach((mid) => {
                  const n = idToNode && idToNode.get(mid)
                  try {
                    selectedItemsRef.current.set(mid, (n && (n.raw || n)) || { id: mid })
                  } catch (e) {}
                })
              } catch (e) {}
            }
          }
        } catch (e) {
          // ignore
        }
      })
      .catch((err) => {})
    return () => {
      mounted = false
    }
  }, [threatItemId, idToNode])

  const roots = filteredTree

  const totalItems = typeof pagination.totalNumber === 'number' ? pagination.totalNumber : roots.length
  const totalPages = pagination.totalPages ?? Math.max(1, Math.ceil(totalItems / pagination.resultsPerPage))
  const pageItems = roots.slice(
    (pagination.currentPage - 1) * pagination.resultsPerPage,
    pagination.currentPage * pagination.resultsPerPage
  )

  const isAllSelected = selectedIds.size > 0 && selectedIds.size === (roots.length || 0)

  const handleSelectAll = () => {
    if (isAllSelected) {
      setSelectedIds(new Set())
      try {
        selectedItemsRef.current.clear()
      } catch (e) {}
      return
    }
    setSelectedIds(new Set(roots.map((r) => r.id)))
  }

  const handleAddToEvaluation = useCallback(async () => {
    const localThreatTimmingId = localStorage.getItem('site_threat_timing_id')
    const extractThreatId = (val) => {
      if (val == null) return null
      if (typeof val === 'string' || typeof val === 'number') return val
      if (typeof val === 'object') {
        return val.threatItemId || val.id || (val.triggerItem && val.triggerItem.id) || null
      }
      return null
    }

    const monitorThreatId = extractThreatId(threatItemId)
    // Build payload using persisted selected items across pages
    const itemsToAdd = []
    try {
      // Prefer persisted selected items; fall back to current selectedIds mapped to nodes
      selectedItemsRef.current.forEach((v, k) => {
        itemsToAdd.push(v)
      })
    } catch (e) {}
    try {
      if (itemsToAdd.length === 0 && idToNode) {
        selectedIds.forEach((id) => {
          const n = idToNode.get(id)
          if (n) itemsToAdd.push(n.raw || n)
        })
      }
      const monitorId = Number((threatItemId && (threatItemId.threatItemId ?? threatItemId)) || threatItemId || null)

      const posts = itemsToAdd.map((item) => {
        const body = [
          {
            monitor_threats_id: monitorId || null,
            siteEcosystemId: item.id || null,
            site_threat_timing_id: Number(localThreatTimmingId) || null,
            site_pressure_id: Number(params.pressureId) || null
          }
        ]
        return postRequest(`/api/monitor-threats-triggers`, body).catch((err) => err)
      })

      // wait for all requests to settle but continue regardless of individual failures
      await Promise.allSettled(posts)

      // After persisting, prefer server-provided authoritative list for display
      let serverArray = null
      if (monitorId) {
        try {
          const resp = await requestAllThreatTriggers(monitorId, 2)
          const extractArrayFromResp = (r) => {
            if (!r) return []
            if (Array.isArray(r)) return r
            if (r.data && Array.isArray(r.data)) return r.data
            const seen = new Set()
            const findArray = (obj) => {
              if (!obj || typeof obj !== 'object') return null
              if (seen.has(obj)) return null
              seen.add(obj)
              for (const k of Object.keys(obj)) {
                const v = obj[k]
                if (Array.isArray(v)) return v
                if (v && typeof v === 'object') {
                  const nested = findArray(v)
                  if (nested) return nested
                }
              }
              return null
            }
            const found = findArray(r)
            return Array.isArray(found) ? found : []
          }
          const arr = extractArrayFromResp(resp)
          if (arr && arr.length > 0) serverArray = arr
        } catch (err) {
          console.error('Failed to refresh triggers after add', err)
        }
      }

      // If server list available use it, otherwise fall back to local merge below
      if (serverArray && serverArray.length > 0) {
        const payload = {
          screencheck: true,
          data: serverArray,
          skipRemovedFilter: true,
          threatItemId: monitorId,
          displayName: threatItemId?.titleValue || modalTitleId || titleValue || '',
          pagination: {
            totalNumber: serverArray.length,
            resultsPerPage: pagination.resultsPerPage,
            totalPages: 1,
            currentPage: pagination.currentPage,
            hasNext: false,
            hasPrevious: false
          }
        }
        try {
          if (typeof onAddToEffect === 'function') onAddToEffect(payload)
        } catch (e) {}
        const siteId = params?.id || site?.id
        const monitoringId = params?.monitoringId
        const safePayload = JSON.parse(JSON.stringify(payload))
        const modalThreatId = monitorId
        if (siteId && monitoringId) {
          navigate(`/site/${siteId}/monitoring/${monitoringId}/pressure`, {
            state: {
              preselected: safePayload,
              openAddDetails: true,
              modalOpen: true,
              modalThreatId,
              returnToPath: location?.pathname || '/',
              openTab: 'ecosystems'
            }
          })
          return
        }
        try {
          if (typeof onAddToEffect === 'function') onAddToEffect(payload)
        } catch (e) {}
        return
      }
    } catch (e) {
      console.error('Error while adding to evaluation', e)
    }
    if (itemsToAdd.length === 0) {
      showErrorToast(<FormattedMessage id="triggerEcosystemNoSelectMessage" />)
      return
    }

    // Merge with any existing preselected payload present in navigation state so
    // previously-added items remain visible alongside newly-added ones.
    const existingPreselected =
      (location && location.state && location.state.preselected) ||
      (window && window.history && window.history.state && window.history.state.preselected) ||
      null

    const normalizeArray = (src) => {
      if (!src) return []
      if (Array.isArray(src)) return src
      if (src && Array.isArray(src.data)) return src.data
      return []
    }

    const getItemId = (it) =>
      (it && (it.id || it.historyId || it.SiteHistoryId || it.siteSpeciesId || it.site_species_id)) || JSON.stringify(it)

    const existingArray = normalizeArray(existingPreselected)
    // build map keyed by id so we can merge without duplicates (prefer existing entries where ids collide)
    const mergedMap = new Map()
    existingArray.forEach((e) => {
      mergedMap.set(getItemId(e), e)
    })
    itemsToAdd.forEach((n) => {
      const key = getItemId(n)
      if (!mergedMap.has(key)) mergedMap.set(key, n)
    })
    const mergedData = Array.from(mergedMap.values())

    const payload = {
      screencheck: true,
      data: mergedData,
      threatItemId: monitorThreatId,
      displayName: threatItemId?.titleValue || modalTitleId || titleValue || '',
      pagination: {
        totalNumber: mergedData.length,
        resultsPerPage: pagination.resultsPerPage,
        totalPages: 1,
        currentPage: pagination.currentPage,
        hasNext: false,
        hasPrevious: false
      }
    }
    // call parent callback and navigate to the pressure page similar to species flow
    try {
      if (typeof onAddToEffect === 'function') onAddToEffect(payload)
    } catch (e) {}

    const siteId = params?.id || site?.id
    const monitoringId = params?.monitoringId
    const safePayload = JSON.parse(JSON.stringify(payload))
    const returnToPath = location?.pathname || '/'
    const modalThreatId = monitorThreatId
    if (siteId && monitoringId) {
      navigate(`/site/${siteId}/monitoring/${monitoringId}/pressure`, {
        state: {
          preselected: safePayload,
          openAddDetails: true,
          modalOpen: true,
          modalThreatId,
          returnToPath,
          openTab: 'ecosystems'
        }
      })
      return
    }

    // Fallback: call parent with payload only
    try {
      if (typeof onAddToEffect === 'function') onAddToEffect(payload)
    } catch (e) {}
  }, [
    selectedIds,
    onAddToEffect,
    threatItemId,
    pagination.resultsPerPage,
    pagination.currentPage,
    idToNode,
    params,
    site,
    location,
    navigate
  ])

  const handleToggleNode = useCallback((node) => {
    // node is an object from the tree
    const nodeId = node.id
    const currentlySelected = selectedIds.has(nodeId)
    const nextSelected = new Set(selectedIds)

    const toggleDescendants = (n) => {
      nextSelected[currentlySelected ? 'delete' : 'add'](n.id)
      if (n.children && n.children.length) n.children.forEach((c) => toggleDescendants(c))
    }

    if (currentlySelected) {
      // remove node and descendants
      const walk = (n) => {
        nextSelected.delete(n.id)
        try {
          selectedItemsRef.current.delete(n.id)
        } catch (e) {}
        if (n.children && n.children.length) n.children.forEach(walk)
      }
      walk(node)
    } else {
      // add node and descendants
      const walk = (n) => {
        nextSelected.add(n.id)
        try {
          selectedItemsRef.current.set(n.id, n.raw || n)
        } catch (e) {}
        if (n.children && n.children.length) n.children.forEach(walk)
      }
      walk(node)
    }

    setSelectedIds(nextSelected)
  })

  // count only species selections (exclude group ids)
  const selectedEcosystemCount = useMemo(() => {
    try {
      // Build a set of ecosystem ids from persisted selections across pages
      const ecoSysIds = new Set()
      try {
        selectedItemsRef.current.forEach((v, k) => {
          ecoSysIds.add(k)
        })
      } catch (e) {}
      // Also include any ecosystem ids that are in the current idToNode map and selectedIds
      selectedIds.forEach((id) => {
        const node = idToNode ? idToNode.get(id) : null
        if (node) ecoSysIds.add(id)
      })

      return ecoSysIds.size
    } catch (e) {
      return 0
    }
  }, [selectedIds, idToNode])

  // report selection count to parent when it changes
  useEffect(() => {
    if (typeof onSelectionChange === 'function') {
      onSelectionChange({ species: 0, ecosystems: selectedEcosystemCount })
    }
  }, [selectedEcosystemCount, onSelectionChange])

  /* -------------------------
      Render helpers (recursive)
  ----------------------------*/
  const renderTreeRow = (node, depth = 0, isChild = false) => {
    const isExpanded = expandedIds.has(node.id)
    const hasChildren = Boolean(node.children && node.children.length)
    const checked = selectedIds.has(node.id)

    return (
      <React.Fragment key={node.id}>
        <SpeciesRow
          depth={depth}
          style={{
            padding: '6px 0px',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            minHeight: 44
          }}
        >
          <div className="left" style={{ display: 'flex', alignItems: 'center', gap: 8, flex: 1, whiteSpace: 'nowrap' }}>
            {isChild && (
              <span>
                <CheckboxArrowIcon />
              </span>
            )}
            <Checkbox
              checked={checked}
              onChange={() => handleToggleNode(node)}
              label={
                <span className="species-label">
                  {/* <span className="status-dot blue" /> */}
                  <CircleIcon width="18" height="18" />
                  {node.name}
                </span>
              }
            />
          </div>
        </SpeciesRow>
        {/* render children only when expanded */}
        {hasChildren && isExpanded && <div>{node.children.map((child) => renderTreeRow(child, depth + 1, true))}</div>}
      </React.Fragment>
    )
  }

  return (
    <Container>
      <Row>
        <Col md={12}>
          <BiodiversitySelectorWrapper>
            <PageSubtitleWrapper style={{ marginTop: 20, justifyContent: 'flex-start', marginBottom: 0 }}>
              <PageSubtitle style={{ borderBottom: 'none', fontSize: '14px', fontWeight: 'normal' }}>
                <FormattedMessage id="selectTriggerSpeciesSubtitle" />
              </PageSubtitle>
              <Tooltip
                placement="bottom"
                trigger={['hover']}
                align={{ offset: [0, 16] }}
                zIndex={9999}
                overlay={{
                  label: 'selectTriggerSpeciesSubtitle',
                  infoTooltip: 'selectTriggerEcosystemInfo'
                }}
                type="big"
              >
                <TooltipHintWrapper>
                  <InfoIcon />
                </TooltipHintWrapper>
              </Tooltip>
            </PageSubtitleWrapper>

            <ActionsRow style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 12 }}>
              <div style={{ display: 'flex', alignItems: 'center' }}>
                {totalItems > 0 && (
                  <Checkbox
                    label={<FormattedMessage id="selectAll" defaultMessage="selectAll" />}
                    checked={isAllSelected}
                    onChange={handleSelectAll}
                  />
                )}
              </div>

              <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                {totalItems > 0 && (
                  <div>
                    <Button
                      type="primary"
                      withIcon
                      height="40px"
                      marginTop="0"
                      disabled={selectedIds.size === 0}
                      isDisabled={totalItems === 0}
                      onClick={handleAddToEvaluation}
                      style={{ justifyContent: 'flex-end' }}
                    >
                      <AddNew />
                      <FormattedMessage
                        id="addEcosystemTriggersToEvaluationBtn"
                        defaultMessage="addEcosystemTriggersToEvaluationBtn"
                      />
                    </Button>
                  </div>
                )}
                {!totalTriggers && (
                  <div>
                    <ViewButton
                      onClick={() => {
                        onCancel?.(false)
                      }}
                    >
                      <ButtonContent>
                        <IconSlot>
                          <ViewIcon />
                        </IconSlot>
                        <TextSlot>
                          <FormattedMessage id="viewStateEvaluation" />
                        </TextSlot>
                      </ButtonContent>
                    </ViewButton>
                  </div>
                )}
              </div>
            </ActionsRow>

            <SpeciesList>
              {loading ? (
                <div style={{ display: 'flex', justifyContent: 'center' }}>
                  <Loader />
                </div>
              ) : totalItems === 0 ? (
                <div style={{ padding: 32, textAlign: 'center', color: '#666' }}>
                  <FormattedMessage id="noRecordsFound" defaultMessage="noRecordsFound" />
                </div>
              ) : (
                <>{pageItems.map((node) => renderTreeRow(node))}</>
              )}
            </SpeciesList>
            {totalItems > 0 && (
              <PaginationWrapper>
                <ItemsPerPageDropdown
                  resultsPerPage={pagination.resultsPerPage}
                  totalNumber={totalItems}
                  currentPage={pagination.currentPage}
                  onChange={(option) => {
                    setPagination((prev) => ({ ...prev, resultsPerPage: option.value, currentPage: 1 }))
                  }}
                  menuPlacement={'top'}
                />
                {totalPages > 1 && (
                  <Pagination
                    activePages={pagination.currentPage}
                    numberOfPages={totalPages}
                    nextPage={() => {
                      if (loading) return
                      setPagination((prev) => ({ ...prev, currentPage: Math.min(totalPages, prev.currentPage + 1) }))
                    }}
                    prevPage={() => {
                      if (loading) return
                      setPagination((prev) => ({ ...prev, currentPage: Math.max(1, prev.currentPage - 1) }))
                    }}
                    selectPage={(page) => {
                      if (loading || page === pagination.currentPage) return
                      setPagination((prev) => ({ ...prev, currentPage: page }))
                    }}
                  />
                )}
              </PaginationWrapper>
            )}
          </BiodiversitySelectorWrapper>
        </Col>
      </Row>
    </Container>
  )
}

export default TriggerEcosystemsTab
